import { earningsBarTime, earningsFutureOffset, earningsShortDate } from './earningsDates.js'

// Native price-pane drawing: the canvas supplies its current media size on each
// redraw, including pane resizing and moving between displays with different DPR.
export class EarningsPrimitive {
  constructor({ events, next, timeframe, timezone, tradingCalendar, onSelect, ink }) {
    Object.assign(this, { events, next, timeframe, timezone, tradingCalendar, onSelect, ink })
    this.groups = []
    this.mapped = []
    this.views = [{ zOrder: () => 'top', renderer: () => this }]
    this.onData = type => {
      if (type === 'full' || Date.now() - this.mappedAt >= 60_000) this.mapEvents()
      this.requestUpdate?.()
    }
    this.onClick = param => {
      if (param.paneIndex !== this.series?.getPane().paneIndex()) return
      const group = this.groupAt(param.point?.x, param.point?.y)
      if (group) this.onSelect(group.events)
    }
  }
  attached({ chart, series, requestUpdate }) {
    Object.assign(this, { chart, series, requestUpdate })
    this.mapEvents()
    series.subscribeDataChanged(this.onData)
    chart.subscribeClick(this.onClick)
    requestUpdate()
  }
  detached() {
    const { series, chart } = this
    this.groups = []
    this.chart = this.series = this.requestUpdate = null
    for (const unsubscribe of [() => series?.unsubscribeDataChanged(this.onData), () => chart?.unsubscribeClick(this.onClick)]) {
      try { unsubscribe() } catch (error) {
        if (error.message !== 'Object is disposed') throw error
      }
    }
  }
  mapEvents() {
    const bars = this.series.data()
    const lastTime = bars.at(-1)?.time
    this.mapped = this.events.map(event => ({ event, lastTime,
      time: earningsBarTime(event, bars, this.timeframe, this.timezone),
      offset: event === this.next ? earningsFutureOffset(event, lastTime, this.timeframe, this.tradingCalendar) : null,
    }))
    this.mappedAt = Date.now()
  }
  paneViews() { return this.views }
  anchorFor(date) {
    const group = this.groups.find(item => item.events.some(event => event.date === date))
    const pane = this.series?.getPane().getHTMLElement()
    if (!group || !pane?.isConnected) return null
    const rect = pane.getBoundingClientRect()
    return { x: rect.left + this.chart.priceScale('left').width() + group.x, y: rect.top + group.top }
  }
  groupAt(x, y) {
    return this.groups.find(group => x >= group.left && x <= group.right && y >= group.top && y <= group.top + 16)
  }
  hitTest(x, y) {
    const group = this.groupAt(x, y)
    return group ? { externalId: `earnings:${group.events.map(e => e.date).join(',')}`, zOrder: 'top', cursorStyle: 'pointer' } : null
  }
  draw(target) {
    if (!this.chart) return
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize: { width, height } }) => {
      const scale = this.chart.timeScale()
      this.groups = []
      if (height < 24) return
      for (const { event, time, lastTime, offset } of this.mapped) {
        let x = time == null ? null : scale.timeToCoordinate(time)
        if (x == null && offset != null) {
          const index = scale.timeToIndex(lastTime)
          if (index != null) x = scale.logicalToCoordinate(index + offset)
        }
        if (x == null || x < 12 || x > width - 12) continue
        let group = this.groups.at(-1)
        if (!group || Math.abs(x - group.x) >= 20) {
          group = { x, left: x - 8, right: x + 8, top: height - 20, events: [] }
          this.groups.push(group)
        }
        group.events.push(event)
      }
      const layout = this.chart.options().layout
      const ink = (typeof this.ink === 'function' ? this.ink() : this.ink) || layout.textColor
      const background = layout.background.color || layout.background.bottomColor || '#202123'
      ctx.save()
      ctx.font = '600 9px sans-serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.lineWidth = 1
      for (const group of this.groups) {
        ctx.fillStyle = background
        ctx.strokeStyle = ink
        ctx.beginPath()
        ctx.roundRect(group.x - 7.5, group.top + 0.5, 15, 15, 3)
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = ink
        ctx.fillText(`E${group.events.length > 1 ? group.events.length : ''}`, group.x, group.top + 8)
        if (group.events.includes(this.next)) {
          const label = earningsShortDate(this.next)
          const labelWidth = ctx.measureText(label).width
          const left = group.x + 12 + labelWidth > width - 4
          const labelX = left ? group.x - 12 - labelWidth : group.x + 12
          if (labelX >= 0 && labelX + labelWidth <= width) {
            ctx.fillStyle = background
            ctx.fillRect(labelX - 2, group.top, labelWidth + 4, 16)
            ctx.fillStyle = ink
            ctx.textAlign = 'left'
            ctx.fillText(label, labelX, group.top + 8)
            ctx.textAlign = 'center'
            group.left = Math.min(group.left, labelX - 2)
            group.right = Math.max(group.right, labelX + labelWidth + 2)
          }
        }
      }
      ctx.restore()
    })
  }
}
